import {
  Box,
  Container,
  Paper,
  Table,
  TableContainer,
  Typography,
  Toolbar,
  AppBar,
  Pagination,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  Skeleton,
  Chip,
  Link as MuiLink,
  Tooltip,
  IconButton,
  FormControl,
  Select,
  InputLabel,
  MenuItem,
  Dialog,
  DialogTitle
} from '@mui/material'
import { NavLink, useSearchParams } from 'react-router-dom'
import { Fragment, useEffect, useState } from 'react'
import { SearchInput } from 'features/search-input'
import { useMediaQuery } from 'react-responsive'
import { adminService } from 'shared/api'
import { useQuery } from 'react-query'
import { giftCardView } from 'pages/nav'
import ViewIcon from '@mui/icons-material/EditNote'
import { giftCardStatusBackground, giftCardStatusColor, giftCardStatusText, paymentStatusText } from './status-utils'
import { GiftCardStatus } from 'shared/models'
import CreateIcon from '@mui/icons-material/Add'
import { GiftCardCreateForm } from './gift-card-create-form'

function GiftCardList() {
  const isMobile = useMediaQuery({ query: '(max-width: 640px)' })

  const [searchParams, setSearchParams] = useSearchParams()

  const [giftCardCreateDialogOpen, setGiftCardCreateDialogOpen] = useState(false)

  const [term, setTerm] = useState(searchParams.get('term') || '')
  const [status, setStatus] = useState<GiftCardStatus[]>(
    (searchParams.get('status')?.split(',') as GiftCardStatus[]) || []
  )

  const [page, setPage] = useState(searchParams.get('page') ? Number(searchParams.get('page')) : 1)
  const [pageCount, setPageCount] = useState(1)

  useEffect(() => {
    const search = new URLSearchParams(searchParams)
    if (term) {
      search.set('term', term)
    } else {
      search.delete('term')
    }

    if (status.length) {
      search.set('status', status.join(','))
    } else {
      search.delete('status')
    }

    setSearchParams(search)
  }, [term])

  const { data, isLoading, refetch } = useQuery(
    ['giftCardList', page, term, status],
    ({ signal }) => adminService.giftCard.getGiftCardList({ term, page, status, limit: 100 }, { signal }),
    {
      onSuccess: (data) => {
        setPageCount(data.data?.pageCount || 1)
      }
    }
  )

  function handleTermChange(e: React.ChangeEvent<HTMLInputElement>) {
    setTerm(e.target.value)
    setPage(1)
  }

  function handlePageChange(page: number) {
    setPage(page)
    const search = new URLSearchParams(searchParams)
    search.set('page', String(page))
    setSearchParams(search)
  }

  function handleCreate() {
    setGiftCardCreateDialogOpen(true)
  }

  function closeCreateDialog() {
    setGiftCardCreateDialogOpen(false)
    refetch()
  }

  return (
    <>
      <AppBar
        position="sticky"
        color="inherit"
        style={{ margin: '-1.5rem -1.5rem 1.5rem', top: isMobile ? '56px' : '4rem', width: 'auto' }}
        elevation={1}
      >
        <Toolbar style={{ display: 'flex', gap: '1rem' }}>
          <Tooltip title="Создать новый сертификат">
            <IconButton size="small" onClick={handleCreate}>
              <CreateIcon />
            </IconButton>
          </Tooltip>
          <SearchInput value={term} onChange={handleTermChange} onClear={() => setTerm('')} withoutLabel={true} />
          <FormControl variant="standard" style={{ minWidth: '200px', marginTop: '-1rem' }}>
            <InputLabel size="small">Статус</InputLabel>
            <Select
              multiple
              value={status}
              onChange={(ev) => {
                if (ev.target.value?.length > 0) {
                  setStatus(ev.target.value as GiftCardStatus[])
                } else {
                  setStatus([])
                }
                setPage(1)
              }}
            >
              <MenuItem key="Created" value={'Created'}>
                {giftCardStatusText.Created}
              </MenuItem>
              <MenuItem key="Active" value={'Active'}>
                {giftCardStatusText.Active}
              </MenuItem>
              <MenuItem key="Used" value={'Used'}>
                {giftCardStatusText.Used}
              </MenuItem>
              <MenuItem key="Expired" value={'Expired'}>
                {giftCardStatusText.Expired}
              </MenuItem>
            </Select>
          </FormControl>
        </Toolbar>
      </AppBar>
      <Container maxWidth="lg" style={{ padding: isMobile ? 0 : undefined }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', ml: 1, mb: 2 }}>
          <Typography color="GrayText" variant="body2">
            {data?.data?.list?.length || 0} из {data?.data?.total || 0} результатов
          </Typography>
        </Box>
        <Paper sx={{ width: '100%', overflow: 'hidden', display: 'grid' }}>
          <TableContainer>
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow>
                  <TableCell style={{ width: '80px' }}></TableCell>
                  <TableCell style={{ width: '140px' }}>Номер</TableCell>
                  <TableCell style={{ minWidth: '140px' }}>Клиент</TableCell>
                  <TableCell style={{ width: '140px' }}>Срок действия</TableCell>
                  <TableCell style={{ width: '80px' }}>Номинал</TableCell>
                  <TableCell style={{ width: '100px' }}>Оплата</TableCell>
                  <TableCell style={{ width: '170px' }}>Статус</TableCell>
                  <TableCell style={{ width: '120px' }}>Остаток</TableCell>
                  <TableCell style={{ width: '60px' }}></TableCell>
                </TableRow>
              </TableHead>
              {!!isLoading && (
                <TableBody>
                  {[...Array(7)].map((_, i) => (
                    <TableRow key={i} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                      {[...Array(9)].map((_, index) => (
                        <TableCell key={index}>
                          <Skeleton />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              )}
              {!isLoading && (
                <TableBody>
                  {!data?.data?.list?.length && (
                    <TableRow>
                      <TableCell colSpan={8} style={{ textAlign: 'center' }}>
                        Ничего не найдено
                      </TableCell>
                    </TableRow>
                  )}
                  {data?.data?.list?.map?.((giftCard) => {
                    return (
                      <Fragment key={giftCard.id}>
                        {giftCard.staffComment && (
                          <TableRow sx={{ '& td, & th': { border: 0 } }}>
                            <TableCell />
                            <TableCell colSpan={7} style={{ padding: '0.5rem 0.5rem 0' }}>
                              <Chip label={giftCard.staffComment} size="small" variant="outlined" />
                            </TableCell>
                          </TableRow>
                        )}
                        <TableRow
                          key={giftCard.id}
                          sx={{
                            borderTop: 'none',
                            '&:last-child td, &:last-child th': { border: 0 },
                            '& td': giftCard.staffComment ? { padding: '0.5rem 1rem 1rem' } : undefined
                          }}
                        >
                          <TableCell style={{ verticalAlign: 'top', width: '80px', maxWidth: '80px' }}>
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '0.75rem',
                                justifyContent: 'space-between',
                                flex: 1
                              }}
                            >
                              <div>
                                <div style={{ fontSize: '12px', opacity: 0.64 }}>
                                  {Intl.DateTimeFormat('ru', {
                                    year: 'numeric',
                                    month: '2-digit',
                                    day: '2-digit'
                                  }).format(giftCard.dateTime)}
                                </div>
                                <div style={{ fontSize: '13px', opacity: 0.64 }}>
                                  {Intl.DateTimeFormat('ru', {
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  }).format(giftCard.dateTime)}
                                </div>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell style={{ verticalAlign: 'top', width: '140px', maxWidth: '140px' }}>
                            <Typography variant="body2" fontSize={12}>
                              {giftCard.code}
                            </Typography>
                          </TableCell>
                          <TableCell
                            style={{
                              minWidth: '140px',
                              verticalAlign: 'top',
                              wordBreak: 'break-word'
                            }}
                          >
                            <div>{giftCard.name}</div>
                            <div>
                              <MuiLink style={{ fontSize: '13px' }} href={`tel:${giftCard.phoneNumber}`}>
                                {giftCard.phoneNumber}
                              </MuiLink>
                            </div>
                            <div>
                              <MuiLink style={{ fontSize: '11px' }} href={`mailto:${giftCard.email}`}>
                                {giftCard.email}
                              </MuiLink>
                            </div>
                          </TableCell>
                          <TableCell style={{ verticalAlign: 'top', width: '140px', maxWidth: '140px' }}>
                            <Typography variant="body2">
                              {Intl.DateTimeFormat('ru', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric'
                              }).format(giftCard.expiryDate)}
                            </Typography>
                          </TableCell>
                          <TableCell style={{ verticalAlign: 'top', width: '80px', maxWidth: '80px', opacity: 0.48 }}>
                            <Typography variant="body2">{giftCard.amount} ₽</Typography>
                          </TableCell>
                          <TableCell style={{ verticalAlign: 'top', width: '100px', maxWidth: '100px' }}>
                            {paymentStatusText[giftCard.paymentStatus]}
                          </TableCell>
                          <TableCell style={{ verticalAlign: 'top', width: '170px', maxWidth: '170px' }}>
                            {!!giftCard.status && (
                              <Chip
                                label={giftCardStatusText[giftCard.status]}
                                size="small"
                                style={{
                                  backgroundColor: giftCardStatusBackground[giftCard.status],
                                  color: giftCardStatusColor[giftCard.status]
                                }}
                              />
                            )}
                          </TableCell>
                          <TableCell style={{ verticalAlign: 'top', width: '120px', maxWidth: '120px' }}>
                            <Typography variant="body2" sx={{ color: giftCard.balance > 0 ? '#000' : '#0000006b' }}>
                              {giftCard.balance} ₽
                            </Typography>
                          </TableCell>
                          <TableCell style={{ verticalAlign: 'top', width: '60px', maxWidth: '60px' }}>
                            <Tooltip title="Просмотреть информацию о сертификате">
                              <NavLink to={giftCardView(String(giftCard.id))}>
                                <IconButton size="small">
                                  <ViewIcon fontSize="small" />
                                </IconButton>
                              </NavLink>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      </Fragment>
                    )
                  })}
                </TableBody>
              )}
            </Table>
          </TableContainer>
        </Paper>
        {!isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Pagination count={pageCount} page={page} onChange={(_, page) => handlePageChange(page)} color="primary" />
          </Box>
        )}
      </Container>
      <Dialog open={giftCardCreateDialogOpen} onClose={closeCreateDialog} fullWidth>
        <DialogTitle>Создание сертификата</DialogTitle>
        <GiftCardCreateForm onClose={closeCreateDialog} />
      </Dialog>
    </>
  )
}

export { GiftCardList }
